// Defaultní skupina stanic pro zjednodušenou stránku „Grafy stanic" (/station-charts,
// varianta B), viz doc/plans/deploy-variant-b-station-charts.md a
// cw-server/doc/plans/deploy-config-default-simple-group.md. Instance ji identifikuje
// přes deploy.defaultSimpleGroupTypeId/...Code (station_group_type, viz useDeployConfigStore) —
// pod ním leží plochá množina station_group řádků (PROFI/OBS/NOOBS/OSTATNI pro referenční
// hodnotu 14), ne víceúrovňová hierarchie. GET /api/geography je na serveru už automaticky
// omezené na tuto skupinu, takže tenhle hook slouží jen k obarvení stanic podle jednotlivých
// station_group (mapa i combobox používají stejný výsledek).
import { useMemo } from 'react'
import { useDeployConfigStore } from '../store/useDeployConfigStore'
import { useStationGroupsByTypeCode, useStationGroupMembers } from './useStationGroups'
import { buildGroupColorMap } from '../utils/groupColors'

// Vrací { ready, colorByGhId, legend } — colorByGhId: Map<gh_id, color[]> (viz buildGroupColorMap),
// legend: [{ color, label }]. ready=false dokud instance nemá defaultSimpleGroupTypeCode
// nastavené, nebo dokud nejsou dotažená data — volající (mapa/combobox) v tom případě
// zobrazuje stanice needobarvené / combobox nemá co filtrovat.
export function useDefaultSimpleGroup() {
  const typeCode = useDeployConfigStore(s => s.defaultSimpleGroupTypeCode)

  const { data: groups, isReady: groupsReady } = useStationGroupsByTypeCode(typeCode, { withStatus: true })

  const groupIds = useMemo(() => groups.map(g => g.id), [groups])
  const { data: memberRows, isReady: membersReady } = useStationGroupMembers(groupIds, { withStatus: true })

  const memberGhIds = useMemo(() => {
    const map = new Map()
    for (const row of memberRows) {
      if (!map.has(row.group_id)) map.set(row.group_id, [])
      map.get(row.group_id).push(row.gh_id)
    }
    return map
  }, [memberRows])

  const children = useMemo(
    () => groups.map(g => ({ label: g.label, ghIds: new Set(memberGhIds.get(g.id) ?? []) })),
    [groups, memberGhIds]
  )

  const { colorByGhId, legend } = useMemo(
    () => (children.length > 0 ? buildGroupColorMap(children) : { colorByGhId: new Map(), legend: [] }),
    [children]
  )

  return {
    ready: !typeCode || (groupsReady && membersReady),
    colorByGhId,
    legend,
  }
}
